import React, { useRef, useState } from 'react';
import { Button, Dialog, TextField } from '@biq/apex-ui';
import { DialogContent, Grid, Typography } from '@mui/material';
import { ConfirmationModalProps, styles } from './confirmation-modal-helper';
import { themeObject, validationsMessages } from 'lib';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const ConfirmationModal = ({
    confirmationKey,
    confirmationModal,
    hasConfirmationTextField = true,
    handleConfirmationModal,
    confirmationButtonText,
    confirmationCancelButtonText = 'Cancel',
    confirmationCancelButtonArrowIcon = false,
    confirmationHeadingText,
    confirmationSubHeadingText,
    confirmationInputLabelText,
    handleConfirmation,
    isRequiredIconVisible = true,
    children,
}: ConfirmationModalProps): React.JSX.Element => {
    const textFieldContainerRef = useRef<HTMLDivElement | null>(null);
    const [textFieldValue, setTextFieldValue] = useState<string>('');
    const [textFieldError, setTextFieldError] = useState<string>('');

    const onChangeTextField = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setTextFieldError('');
        setTextFieldValue(event.target.value);
    };

    const handleTextFieldValidation = (): void => {
        if (textFieldValue === confirmationKey) {
            setTextFieldError('');
            handleConfirmation();
        } else if (textFieldValue === '') {
            setTextFieldError(validationsMessages.required);
            setTextFieldValue('');
        } else {
            setTextFieldError(`Type ${confirmationKey}`);
            setTextFieldValue('');
        }
    };

    const handleConfirmationButton = (): void => {
        return textFieldContainerRef.current
            ? handleTextFieldValidation()
            : handleConfirmation();
    };

    return (
        <Dialog
            closable={true}
            open={confirmationModal}
            onClose={handleConfirmationModal}
            title={confirmationHeadingText}
            sx={styles.dialogStyles}
        >
            <DialogContent>
                <Grid>{confirmationSubHeadingText}</Grid>
                {hasConfirmationTextField ? (
                    <Grid
                        sx={styles.textFieldContainer}
                        ref={textFieldContainerRef}
                    >
                        <Typography sx={styles.textFieldLabelStyles}>
                            {confirmationInputLabelText}
                            {isRequiredIconVisible && (
                                <Typography
                                    component={'span'}
                                    sx={styles.textFieldRequiredStyles}
                                >
                                    *
                                </Typography>
                            )}
                        </Typography>
                        <TextField
                            variant='outlined'
                            required={true}
                            value={textFieldValue}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ): void => onChangeTextField(event)}
                            errorText={textFieldError}
                            error={!!textFieldError}
                        />
                    </Grid>
                ) : null}
                <Grid>{children}</Grid>
                <Grid sx={styles.buttonContainer}>
                    <Button
                        size='large'
                        color='error'
                        onClick={handleConfirmationButton}
                    >
                        {confirmationButtonText}
                    </Button>
                    <Button
                        size='large'
                        variant='text'
                        onClick={handleConfirmationModal}
                        startIcon={
                            confirmationCancelButtonArrowIcon && (
                                <ArrowBackIcon
                                    color={themeObject.palette.primary[400]}
                                />
                            )
                        }
                    >
                        {confirmationCancelButtonText}
                    </Button>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};
